/**
 * Result of a print attempt. The Expo shell can report definitive success or
 * failure; the browser fallback can only assume success (the browser does not
 * expose a print result to JS).
 */
export type PrintResult =
  | { status: 'ok' }
  | { status: 'unavailable'; reason?: string }
  | { status: 'error'; reason?: string };

/**
 * Bridge injected by the Expo kiosk shell. The shell's WebView injects this
 * global on load; the web app calls it from `printCheckInSlip`.
 */
declare global {
  interface Window {
    thsPrint?: (html: string) => Promise<PrintResult>;
  }
}

/**
 * Print the check-in slip for a successful check-in.
 *
 * Resolution order:
 * 1. If the Expo shell bridge (`window.thsPrint`) is present, use it (silent).
 * 2. Otherwise, render the HTML in a hidden iframe and call `window.print()`
 *    (browser/AirPrint dialog). Used in `pnpm dev` and as a fallback if the
 *    shell bridge is unavailable at runtime.
 *
 * Always returns a result; never throws. Callers display a banner on
 * non-"ok" results.
 */
export async function printCheckInSlip(html: string): Promise<PrintResult> {
  if (typeof window.thsPrint === 'function') {
    return printViaShell(html);
  }
  return printViaBrowser(html);
}

async function printViaShell(html: string): Promise<PrintResult> {
  try {
    const result = await window.thsPrint!(html);
    return result;
  } catch (err) {
    const reason = err instanceof Error ? err.message : String(err);
    return { status: 'error', reason };
  }
}

// Long enough to outlast the user picking settings in the print dialog (most
// print dialogs are dismissed within a minute). Bumped from 5s after we
// learned modern Chrome does not block on iframe.print() the way we'd assumed
// — removing the iframe too early dismisses the dialog before it renders.
const PRINT_FALLBACK_MS = 60_000;

async function printViaBrowser(html: string): Promise<PrintResult> {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.srcdoc = html;

    let settled = false;
    let removeAfterPrintListeners: (() => void) | undefined;
    const cleanup = () => {
      if (settled) return;
      settled = true;
      removeAfterPrintListeners?.();
      iframe.remove();
      resolve({ status: 'ok' });
    };

    iframe.addEventListener('load', () => {
      const win = iframe.contentWindow;
      if (!win) {
        cleanup();
        return;
      }

      // Wait for the print dialog to close before removing the iframe.
      // Removing too early closes the pending dialog on Chrome (which
      // returns from print() before the dialog renders). Chrome dispatches
      // afterprint to the *parent* window when an iframe initiated print,
      // while other browsers dispatch it to the iframe's window — listen
      // on both so cleanup fires whichever way it goes.
      const onAfterPrint = () => cleanup();
      removeAfterPrintListeners = () => {
        win.removeEventListener('afterprint', onAfterPrint);
        window.removeEventListener('afterprint', onAfterPrint);
      };
      win.addEventListener('afterprint', onAfterPrint);
      window.addEventListener('afterprint', onAfterPrint);

      try {
        win.focus();
        win.print();
      } catch {
        cleanup();
      }
    });

    // Fallback for browsers that don't fire afterprint (some mobile WebViews)
    // or cases where the iframe never loads. The iframe is invisible and
    // zero-size, so lingering for the fallback window is harmless.
    // cleanup is idempotent via `settled`, so we don't bother saving the
    // timer id to clear it later.
    setTimeout(cleanup, PRINT_FALLBACK_MS);

    document.body.appendChild(iframe);
  });
}
